import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { fetchAdminTaxRemittanceReport } from '../../services/adminTaxReportService';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCalendar,
  FiDownload,
  FiLoader,
  FiRefreshCw,
  FiShield
} = FiIcons;

const money = (value) => {
  if (value === null || value === undefined) return 'Pending';
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 29);

  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10)
  };
};

const AdminTaxReportPanel = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [report, setReport] = useState({ summary: {}, transactions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async () => {
    setLoading(true);
    setError('');

    try {
      setReport(await fetchAdminTaxRemittanceReport(startDate, endDate));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load the tax remittance report.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const exportRows = useMemo(() => report.transactions.map((row) => ({
    order_id: row.order_reference,
    order_date: row.order_date,
    seller: row.seller,
    buyer: row.buyer,
    product_subtotal: row.product_subtotal,
    tax_collected: row.tax_collected,
    seller_attributable_tax: row.seller_attributable_tax,
    tax_retained_by_sohlocal: row.tax_retained_by_sohlocal,
    refund_amount: row.refund_amount,
    tax_liable_party: row.tax_liable_party,
    tax_status: row.tax_status,
    payment_status: row.payment_status,
    order_status: row.order_status
  })), [report.transactions]);

  const exportReport = () => {
    if (!exportRows.length) return;

    const headers = Object.keys(exportRows[0]);
    const csv = [
      headers,
      ...exportRows.map((row) => headers.map((header) => row[header]))
    ].map((line) => line.map((value) => (
      `"${String(value ?? '').replaceAll('"', '""')}"`
    )).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sohlocal-tax-remittance-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const summary = report.summary || {};

  return (
    <section className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-4xl font-black tracking-tighter text-slate-900">
            Tax <span className="text-indigo-600">Remittance</span>
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Review tax collected by SOHLocal and the amounts attributable to registered sellers.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={loadReport}
            disabled={loading}
            className="flex items-center rounded-2xl bg-indigo-600 px-5 py-3 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
          >
            <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportReport}
            disabled={!exportRows.length}
            className="flex items-center rounded-2xl border border-slate-200 px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-600 disabled:opacity-50"
          >
            <SafeIcon icon={FiDownload} className="mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
          <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
          {error}
        </div>
      )}

      <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="text-xs font-black uppercase tracking-widest text-slate-400">
            From date
            <span className="relative mt-2 block">
              <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="rounded-2xl bg-slate-50 py-4 pl-11 pr-4 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </span>
          </label>
          <label className="text-xs font-black uppercase tracking-widest text-slate-400">
            To date
            <span className="relative mt-2 block">
              <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="rounded-2xl bg-slate-50 py-4 pl-11 pr-4 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </span>
          </label>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-5">
        {[
          ['Tax collected', summary.tax_collected],
          ['Seller-attributable tax', summary.seller_attributable_tax],
          ['Tax retained by SOHLocal', summary.tax_retained_by_sohlocal],
          ['SOHLocal remittance', summary.estimated_sohlocal_remittance],
          ['Refund amounts', summary.refund_amount]
        ].map(([label, value]) => (
          <div key={label} className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
            <p className="mt-2 text-2xl font-black text-slate-900">{money(value)}</p>
          </div>
        ))}
      </div>

      <div className="flex items-start rounded-2xl border border-indigo-100 bg-indigo-50 px-5 py-4 text-sm font-bold text-indigo-800">
        <SafeIcon icon={FiShield} className="mr-3 mt-0.5 text-lg" />
        <span>
          SOHLocal remittance is based on collected tax assigned to SOHLocal under the immutable checkout tax-liability snapshot. Seller-attributable tax is shown separately for seller reporting.
        </span>
      </div>

      <section className="overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-8">
          <h3 className="text-2xl font-black text-slate-900">Tax detail</h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            {summary.finalized_order_count || 0} finalized · {summary.pending_order_count || 0} pending · {summary.failed_order_count || 0} failed
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1500px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
                {[
                  'Order',
                  'Date',
                  'Seller',
                  'Buyer',
                  'Product subtotal',
                  'Tax collected',
                  'Seller tax',
                  'SOHLocal retained',
                  'Refunds',
                  'Liable party',
                  'Tax status'
                ].map((heading) => <th key={heading} className="px-5 py-4">{heading}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="11" className="py-20 text-center">
                    <SafeIcon icon={FiLoader} className="animate-spin text-3xl text-indigo-300" />
                  </td>
                </tr>
              ) : report.transactions.length ? (
                report.transactions.map((row) => (
                  <tr key={row.order_id} className="text-sm">
                    <td className="px-5 py-5 font-black text-indigo-600">{row.order_reference}</td>
                    <td className="px-5 py-5 text-slate-500">{row.order_date}</td>
                    <td className="px-5 py-5 font-bold text-slate-900">{row.seller}</td>
                    <td className="px-5 py-5 text-slate-600">{row.buyer}</td>
                    <td className="px-5 py-5">{money(row.product_subtotal)}</td>
                    <td className="px-5 py-5 font-black">{money(row.tax_collected)}</td>
                    <td className="px-5 py-5 text-emerald-600">{money(row.seller_attributable_tax)}</td>
                    <td className="px-5 py-5 text-indigo-600">{money(row.tax_retained_by_sohlocal)}</td>
                    <td className="px-5 py-5 text-amber-600">{money(row.refund_amount)}</td>
                    <td className="px-5 py-5 text-xs font-black uppercase">{row.tax_liable_party}</td>
                    <td className="px-5 py-5 text-xs font-black uppercase">{row.tax_status}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="py-20 text-center text-sm font-bold text-slate-400">
                    No platform tax transactions were found for this date range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
};

export default AdminTaxReportPanel;